import { AlertTriangle, Clock, FileText, ShieldAlert, Stamp } from 'lucide-react';
import { CUSTOMS_SHIPMENTS, CUSTOMS_STATUS } from './mockComplianceData';

function StatusBadge({ status }) {
  const config = CUSTOMS_STATUS[status];
  return (
    <span
      className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${config.className}`}
    >
      {config.label}
    </span>
  );
}

function HazmatCountdown({ daysRemaining, expiryDate }) {
  const isCritical = daysRemaining <= 7;
  const isWarning = daysRemaining <= 30 && daysRemaining > 7;

  const colorClass = isCritical
    ? 'text-red-700 bg-red-50 ring-red-500/25'
    : isWarning
      ? 'text-amber-700 bg-amber-50 ring-amber-500/25'
      : 'text-logi-muted bg-logi-silver/80 ring-logi-grid';

  return (
    <div className={`inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs ring-1 ${colorClass}`}>
      {isCritical && <AlertTriangle className="h-3 w-3 animate-pulse" />}
      {!isCritical && <Clock className="h-3 w-3" />}
      <span className="font-semibold">{daysRemaining}d left</span>
      <span className="text-[10px] opacity-70">· {expiryDate}</span>
    </div>
  );
}

export default function CustomsHazmatLocker({ shipments = CUSTOMS_SHIPMENTS }) {
  const pendingCount = shipments.filter(
    (s) => s.status === 'pending_documents' || s.hazmatDaysRemaining <= 7
  ).length;

  return (
    <section className="overflow-hidden rounded-2xl border border-logi-grid bg-white backdrop-blur-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-logi-grid px-5 py-4">
        <div className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-logi-accent/100/10 ring-1 ring-blue-500/20">
            <Stamp className="h-4 w-4 text-blue-700" strokeWidth={1.75} />
          </div>
          <div>
            <h2 className="text-sm font-bold text-logi-text">UAE Customs &amp; Hazmat Gate Pass Locker</h2>
            <p className="text-xs text-logi-text0">Border · Port clearance · DG permit tracking</p>
          </div>
        </div>
        {pendingCount > 0 && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-bold text-amber-700 ring-1 ring-amber-500/25">
            <ShieldAlert className="h-3.5 w-3.5" />
            {pendingCount} require action
          </span>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[960px] text-left text-sm">
          <thead>
            <tr className="border-b border-logi-grid/80 text-xs uppercase tracking-wider text-logi-text0">
              <th className="px-5 py-3 font-semibold">Shipment ID</th>
              <th className="px-5 py-3 font-semibold">Client</th>
              <th className="px-5 py-3 font-semibold">Customs Office</th>
              <th className="px-5 py-3 font-semibold">Bill of Entry (BOE)</th>
              <th className="px-5 py-3 font-semibold">Status</th>
              <th className="px-5 py-3 font-semibold">Hazmat Permit</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-logi-grid/30">
            {shipments.map((row) => (
              <tr key={row.id} className="transition-colors hover:bg-white">
                <td className="px-5 py-3.5 font-mono text-sm font-semibold text-white">
                  {row.id}
                </td>
                <td className="px-5 py-3.5 font-semibold text-logi-text">{row.client}</td>
                <td className="px-5 py-3.5 text-logi-muted">{row.customsOffice}</td>
                <td className="px-5 py-3.5">
                  <span className="inline-flex items-center gap-1.5 font-mono text-xs text-logi-muted">
                    <FileText className="h-3.5 w-3.5 text-logi-text0" />
                    {row.boeNumber}
                  </span>
                </td>
                <td className="px-5 py-3.5">
                  <StatusBadge status={row.status} />
                </td>
                <td className="px-5 py-3.5">
                  <HazmatCountdown
                    daysRemaining={row.hazmatDaysRemaining}
                    expiryDate={row.hazmatPermitExpiry}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
